<template>
  <!-- FT-1501 (the L.O.T.S. half of FT-1497; user: "Give L.O.T.S. the same
       character preview, so both games share this layout"): ONE PIECE OF
       NIGHT TEXT ON THE TABLE — the player's prompt, the sealed line, a told
       sentence, the checklist line, a sheet label. Clocktower's component,
       carried over whole.

       Everywhere but the Lot editor's preview this is the plain element it
       replaced: the same tag, the same classes and attributes from the
       parent, the text and nothing else — a real town never sees anything
       more. While the editor's preview runs (golem/nightLab `copy`, handed in
       by the editor and dropped at exit) the text of the Lot on the bench
       becomes click-to-edit in place, the FT-1494 card's pattern: a dotted
       underline and a pencil under the pointer, Enter or a click to start,
       Esc or leaving the box to finish. Every keystroke goes through that one
       door into the editor's form, so Save / Discard, the unsaved-changes
       ask and the copy cap are the form's, untouched, and the table repaints
       as the words change. -->
  <!-- The three faces are KEYED so each is its own element: patched in place,
       the rest face's click-to-start would take over the editing face's
       listener mid-event, and the click that ends an edit (Reset) would
       start it again. -->
  <component :is="tag" v-if="!editor" key="plain">{{ text }}</component>
  <component
    :is="tag"
    v-else-if="editing"
    key="editing"
    class="nct nct-editing"
    :class="{ 'nct-multi': multi }"
    @click.stop
    @pointerdown.stop
  >
    <textarea
      v-if="multi"
      ref="input"
      class="nct-input"
      rows="1"
      :value="draft"
      :maxlength="max"
      :placeholder="placeholder"
      :aria-label="label"
      spellcheck="true"
      @input="onInput"
      @keydown.enter.prevent="finish(true)"
      @keydown.esc.stop.prevent
      @keyup.esc.stop="finish(true)"
      @blur="finish(false)"
    ></textarea>
    <input
      v-else
      ref="input"
      class="nct-input"
      :value="draft"
      :size="inputSize"
      :maxlength="max"
      :placeholder="placeholder"
      :aria-label="label"
      @input="onInput"
      @keydown.enter.prevent="finish(true)"
      @keydown.esc.stop.prevent
      @keyup.esc.stop="finish(true)"
      @blur="finish(false)"
    />
    <!-- the box's one-line hint and its way back to the default. Pressing
         here never takes the focus from the box (a blur would end the edit
         before the click lands). -->
    <span class="nct-tools" @pointerdown.prevent @mousedown.prevent>
      <span class="nct-hint">{{ hint }}</span>
      <!-- FT-1536: THE WAY BACK — the same Reset serves a line left empty
           on purpose: it is the author's choice like any words, and this
           puts the default back. -->
      <button
        type="button"
        class="nct-reset"
        :disabled="!own"
        :title="
          blank
            ? 'Stop leaving it empty — the default comes back'
            : 'Clear your words — the default comes back'
        "
        @click="reset"
      >
        Reset to default
      </button>
    </span>
  </component>
  <component
    :is="tag"
    v-else
    key="rest"
    class="nct"
    :class="{ 'nct-default': !own, 'nct-own': own, 'nct-empty': !text }"
    role="button"
    tabindex="0"
    :aria-label="
      label +
      (blank ? ' (left empty)' : own ? ' (your words)' : ' (default)') +
      ': ' +
      (text || '—')
    "
    @click.stop="start"
    @pointerdown.stop
    @keydown.enter.prevent="start"
    @keydown.space.prevent="start"
    >{{ text || emptyHint
    }}<span
      v-if="own"
      class="nct-dot"
      aria-hidden="true"
      :title="blank ? 'Left empty on purpose' : 'Your words — not the default'"
    ></span
  ></component>
</template>

<script>
import { nightLab } from "../golem/nightLab";
import { BLANKABLE_KEYS } from "../golem/nightSigns";

/** The copy keys in the author's words (the Studio's old Night text
 *  labels), for the box's name. FT-1532: the face's per-state words — the
 *  ask and the line under it, once per stepper state. */
const LABELS = {
  line: "Checklist line",
  invite: "Player's prompt",
  sealed: "After they confirm",
  telling: "Told (bright)",
  settled: "Told (after)",
  seats: "Seats label",
  told: "Told label",
  picked: "Prompt once picked",
  sent: "Prompt once answered",
  lineInvite: "Line at invitation",
  linePicked: "Line once picked",
  lineSealed: "Line once confirmed",
  lineSent: "Line once answered",
};
/** The face's asks — their `{names}` is the picked players. */
const ASKS = ["invite", "picked", "sealed", "sent"];
/** The sheet's two labels are words; everything else is a sentence. */
const SINGLE = ["seats", "told"];
/** The told sentences have no default TEMPLATE (toldInfo composes the
 *  sentence from the telling), so their box never starts from one. */
const COMPOSED = ["telling", "settled"];

export default {
  name: "NightCopyText",
  props: {
    /** The element this text is (and stays, outside the editor). */
    tag: { type: String, default: "span" },
    /** What the table shows right now — rendered, tokens filled. */
    text: { type: String, default: "" },
    /** Which copy key wrote it; "" when the words on show are the control's
     *  own (a count, a confirm hint) — never editable. */
    copyKey: { type: String, default: "" },
    /** Whose night this text belongs to — editable only for the lab's Lot
     *  (the one on the editor's bench). */
    roleId: { type: String, default: "" },
    /** The default the empty words stand for, where the surface's default is
     *  not the copy layer's (the sheet's single label keeps the Lot's own
     *  verb); the lab's shipped default otherwise. */
    defaultText: { type: String, default: "" },
  },
  data() {
    return {
      editing: false,
      draft: "",
      /** FT-1540: what the face showed when this edit began, while the key
       *  was on its default — the EFFECTIVE default, as the face itself
       *  composed it (the generic invite, the Picked hint, the generic seal
       *  are the face's sentences, not the copy layer's, so `fallback`
       *  cannot see them). "" when the key held the author's words. */
      shownDefault: "",
      /** FT-1540: the key was left empty on purpose when this edit began. */
      blankAtStart: false,
      /** FT-1540: the face showed words when this edit began (the author's
       *  or any default) — emptying the box then means "show nothing". */
      shownAtStart: false,
    };
  },
  computed: {
    /** The editor's door, while its preview shows THIS Lot's night — null
     *  everywhere else (every real town among them). */
    editor() {
      const lab = nightLab;
      if (!lab.open || !lab.studio || !lab.copy) return null;
      if (!this.copyKey || !this.roleId || this.roleId !== lab.roleId) {
        return null;
      }
      return lab.copy;
    },
    multi() {
      return SINGLE.indexOf(this.copyKey) === -1;
    },
    label() {
      return LABELS[this.copyKey] || "Night text";
    },
    /** A line with no words at all (a new Lot's checklist line, or one
     *  left empty on purpose) still offers itself in the editor, so it can
     *  be written. FT-1536: worded AND styled as a hint (`.nct-empty`),
     *  never as the line itself. */
    emptyHint() {
      return this.blank ? "Empty · " + this.label : "+ " + this.label;
    },
    /** FT-1536: may this key be left empty on purpose (copy layer's rule)? */
    blankable() {
      return BLANKABLE_KEYS.indexOf(this.copyKey) !== -1;
    },
    max() {
      return (this.editor && this.editor.max) || 200;
    },
    /** The form's own words for this key — null on its default, "" (or
     *  blank space) where it was left empty on purpose (FT-1536). */
    stored() {
      if (!this.editor) return null;
      const v = this.editor.raw(this.copyKey);
      return typeof v === "string" ? v : null;
    },
    raw() {
      return this.stored || "";
    },
    /** FT-1536: left empty on purpose — nothing shows in a town. */
    blank() {
      return this.blankable && this.stored !== null && !this.stored.trim();
    },
    own() {
      return !!this.raw.trim() || this.blank;
    },
    /** The shipped default the empty words stand for. */
    fallback() {
      if (!this.editor) return "";
      return this.defaultText || this.editor.fallback(this.copyKey) || "";
    },
    /** FT-1540: THIS KEY'S EFFECTIVE DEFAULT — the copy layer's template
     *  where it has one, else the sentence the face showed on its default
     *  (one source of truth: the face; no second copy of its sentences
     *  here). */
    effectiveDefault() {
      if (COMPOSED.indexOf(this.copyKey) !== -1) return this.fallback;
      return this.fallback || this.shownDefault;
    },
    /** The box starts from the words standing now: the author's, or the
     *  default's template (never a composed told sentence) — FT-1540: or
     *  the face's own sentence where the default is composed there. */
    startText() {
      if (this.own) return this.raw;
      return COMPOSED.indexOf(this.copyKey) === -1 ? this.effectiveDefault : "";
    },
    /** FT-1536: an emptied box of a blankable key says what empty MEANS
     *  (nothing shows) — it no longer wears the default, which it would not
     *  bring back. */
    placeholder() {
      if (this.blankable && (this.effectiveDefault || this.blankAtStart)) {
        return "Empty — nothing shows here";
      }
      return this.fallback || this.text || "Write it";
    },
    inputSize() {
      return Math.max(this.draft.length, this.placeholder.length, 4) + 1;
    },
    hint() {
      // FT-1536: empty now MEANS empty on these keys; Reset is the way back
      if (this.copyKey === "line" && this.blank) {
        return "Players see nothing · the sheet keeps the reminder";
      }
      if (ASKS.indexOf(this.copyKey) !== -1) {
        return "{names} is the picked players · empty shows nothing";
      }
      // FT-1532: a confirmed or answered line has no words by default — the
      // chips of the picks stand in its place
      if (this.copyKey === "lineSealed" || this.copyKey === "lineSent") {
        return "Empty shows the picks as chips";
      }
      if (this.blankable) return "Empty shows nothing";
      if (COMPOSED.indexOf(this.copyKey) !== -1) {
        return "{names} the players · {number} · {character} what they're told";
      }
      return "Empty uses the default";
    },
  },
  watch: {
    // the preview stopped, or this element moved to other words: the edit
    // is over
    editor(ed) {
      if (!ed) this.editing = false;
    },
    copyKey() {
      this.editing = false;
    },
  },
  methods: {
    start() {
      if (!this.editor || this.editing) return;
      // FT-1540: snapshot the face's words before the first keystroke
      // repaints them
      this.blankAtStart = this.blank;
      this.shownDefault = this.own ? "" : String(this.text || "");
      this.shownAtStart = !!this.raw.trim() || !!String(this.text || "").trim();
      this.draft = this.startText;
      this.editing = true;
      this.$nextTick(() => {
        const el = this.$refs.input;
        if (!el) return;
        el.focus();
        const n = el.value.length;
        el.setSelectionRange(n, n);
        if (this.multi) this.grow(el);
      });
    },
    /** Words equal to the default ARE the default — stored as null.
     *  FT-1536: an emptied box is stored as "" (left empty on purpose) where
     *  the key may be blank and there is a default to switch off; anywhere
     *  else empty still means the default.
     *  FT-1540: "a default to switch off" is whatever the face SHOWED when
     *  the edit began — the author's words, the copy layer's default, or
     *  the face's own generic sentence — and a line already left empty
     *  stays empty. */
    toStored(value) {
      const v = String(value || "");
      const t = v.trim();
      const d = this.effectiveDefault.trim();
      if (d && COMPOSED.indexOf(this.copyKey) === -1 && t === d) return null;
      if (!t) {
        const switchOff = this.blankAtStart || this.shownAtStart || !!d;
        return this.blankable && switchOff ? "" : null;
      }
      return v;
    },
    onInput(e) {
      this.draft = e.target.value;
      if (this.multi) this.grow(e.target);
      if (this.editor) {
        this.editor.write(this.copyKey, this.toStored(this.draft));
      }
    },
    /** Close the box. `refocus` (Enter, Esc, Reset) hands the focus back to
     *  the text, so the keyboard keeps its place. */
    finish(refocus) {
      if (!this.editing) return;
      if (this.editor) {
        this.editor.write(this.copyKey, this.toStored(this.draft));
      }
      this.editing = false;
      if (!refocus) return;
      this.$nextTick(() => {
        if (this.$el && this.$el.focus) this.$el.focus();
      });
    },
    reset() {
      if (!this.editor) return;
      this.draft = "";
      this.editor.write(this.copyKey, null);
      this.editing = false;
      this.$nextTick(() => {
        if (this.$el && this.$el.focus) this.$el.focus();
      });
    },
    /** A box that grows with its words. */
    grow(el) {
      el.style.height = "auto";
      el.style.height = el.scrollHeight + "px";
    },
  },
};
</script>

<style scoped lang="scss">
@import "../controls.scss";

// the text, click-to-edit — reads as itself until touched. The table's own
// plates can be `pointer-events: none` (the face's centre plate is, bar its
// controls), so this re-arms the pointer on the text alone.
.nct {
  pointer-events: auto;
  cursor: $control-edit-cursor, text;
  border-radius: 3px;
  text-underline-offset: 3px;
  &:hover {
    text-decoration: underline dotted rgba(255, 255, 255, 0.6);
  }
  &:focus-visible {
    @include control-focus-ring;
    outline-offset: 2px;
  }
}
// still on its default: a faint dotted underline, so the text reads as
// "editable, and not yet yours"
.nct-default {
  text-decoration: underline dotted rgba(255, 255, 255, 0.28);
}
// no words at all: the hint that offers the line. FT-1536 (project rule:
// placeholder text never impersonates a real value) — it drops the line's own
// dress (italic, bold, the pick ink, the dotted "editable" underline) for the
// Studio's small UI voice in a dashed slot, dim, so it reads as a place to
// write and never as words a player would see. (The class is doubled to
// outrank the face's own line dress, `.nf-ask.settled` among it.)
.nct.nct-empty.nct-empty {
  display: inline-block;
  padding: 0 8px;
  font-family: "Roboto Condensed", sans-serif;
  font-size: 11px;
  font-style: normal;
  font-weight: normal;
  letter-spacing: 0.02em;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.5);
  text-shadow: none;
  text-decoration: none;
  border: 1px dashed rgba(255, 255, 255, 0.28);
  border-radius: 999px;
  &:hover {
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    border-color: rgba(255, 255, 255, 0.55);
  }
}
// the author's own words: a small gold dot after them
.nct-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-left: 5px;
  vertical-align: middle;
  border-radius: 50%;
  background: #e6c27a;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.9);
}

// ── EDITING ─────────────────────────────────────────────────────────────────
.nct-editing {
  position: relative;
  display: inline-block;
  cursor: auto;
  &.nct-multi {
    display: block;
    align-self: stretch;
    width: 100%;
    box-sizing: border-box;
  }
}
// the box wears the text's own size and ink, the app's field chrome off
// (the class pair plus the scope id outrank App.vue's app-wide field rule)
.nct.nct-editing .nct-input {
  display: block;
  box-sizing: border-box;
  max-width: 100%;
  margin: 0;
  padding: 0 2px;
  font: inherit;
  color: inherit;
  text-align: inherit;
  letter-spacing: inherit;
  background: rgba(0, 0, 0, 0.45);
  border: 0;
  border-bottom: 1px dotted rgba(255, 255, 255, 0.6);
  border-radius: 0;
  box-shadow: none;
  &:focus {
    outline: none;
    border-bottom-color: rgba(255, 255, 255, 0.9);
    box-shadow: none;
  }
}
.nct.nct-editing textarea.nct-input {
  width: 100%;
  resize: none;
  overflow: hidden;
}
.nct.nct-editing input.nct-input {
  display: inline-block;
  width: auto;
}
// the hint and the reset, hung under the box so nothing on the table moves
.nct-tools {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  white-space: nowrap;
  font-family: "Roboto Condensed", sans-serif;
  font-size: 11px;
  font-style: normal;
  font-weight: normal;
  letter-spacing: normal;
  line-height: 1.3;
  text-align: left;
  text-shadow: none;
  color: rgba(255, 255, 255, 0.78);
  background: #0a0404;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 6px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.85);
  pointer-events: auto;
}
.nct-reset {
  font: inherit;
  color: #e6c27a;
  background: transparent;
  border: 1px solid rgba(230, 194, 122, 0.55);
  border-radius: 999px;
  padding: 1px 8px;
  cursor: pointer;
  &:hover:not(:disabled) {
    border-color: #e6c27a;
  }
  &:focus-visible {
    @include control-focus-ring;
  }
  &:disabled {
    @include control-disabled;
  }
}

// a finger needs a 40px target on the text, the box and the reset
@media (pointer: coarse) {
  .nct:not(.nct-editing) {
    display: inline-flex;
    align-items: center;
    min-height: 40px;
  }
  .nct.nct-editing .nct-input,
  .nct-reset {
    min-height: 40px;
  }
}
</style>
